import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin } from "lucide-react";

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0a0310] border-t border-[#4b0082]/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-6">
              <span className="text-xl font-bold text-[#00f0ff]">healthcentrixllc.online</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              HEALTHCENTRIX LLC - A trusted multi-specialty medical center providing exceptional healthcare with compassion and innovation across the nation.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full bg-[#1a0b2e] border border-[#4b0082] flex items-center justify-center text-gray-400 hover:text-[#00f0ff] hover:border-[#00f0ff] transition-all"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="#" 
                data-testid="footer-twitter"
                className="w-10 h-10 rounded-full bg-[#1a0b2e] border border-[#4b0082] flex items-center justify-center text-gray-400 hover:text-[#00f0ff] hover:border-[#00f0ff] transition-all"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a 
                href="#" 
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full bg-[#1a0b2e] border border-[#4b0082] flex items-center justify-center text-gray-400 hover:text-[#ff00ff] hover:border-[#ff00ff] transition-all"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href="#" 
                data-testid="footer-linkedin"
                className="w-10 h-10 rounded-full bg-[#1a0b2e] border border-[#4b0082] flex items-center justify-center text-gray-400 hover:text-[#00f0ff] hover:border-[#00f0ff] transition-all"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { to: "/about", label: "About Us" },
                { to: "/services", label: "Our Services" },
                { to: "/doctors", label: "Our Doctors" },
                { to: "/appointment", label: "Book Appointment" },
                { to: "/resources", label: "Patient Resources" },
                { to: "/contact", label: "Contact Us" },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 text-sm hover:text-[#00f0ff] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {[
                "Emergency Care",
                "Primary Care",
                "Pediatrics",
                "Cardiology",
                "Orthopedics",
                "Women's Health",
              ].map((service) => (
                <li key={service}>
                  <Link 
                    to="/services"
                    data-testid={`footer-service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 text-sm hover:text-[#ff00ff] transition-colors"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-6">Contact Info</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#00f0ff] shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  <strong className="text-white">Main Office:</strong><br />
                  2626 Glenwood Ave, Ste 310<br />
                  Raleigh, NC 27608
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#ff00ff] shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  <strong className="text-white">Headquarters:</strong><br />
                  6631 E 2nd St, Ste 100<br />
                  Casper, WY 82609
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#ff00ff] shrink-0" />
                <a 
                  href="tel:8449436175" 
                  data-testid="footer-phone"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  844-943-6175
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#ff9e00] shrink-0" />
                <a 
                  href="mailto:info@healthcentrixllc.online"
                  data-testid="footer-email"
                  className="text-gray-400 text-sm hover:text-white transition-colors"
                >
                  info@healthcentrixllc.online
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#00f0ff] shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  Mon - Fri: 9AM - 5PM<br />
                  Sat - Sun: 11AM - 4PM
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#4b0082]/30 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} HEALTHCENTRIX LLC. All rights reserved.
            </p>
            <span className="hidden md:inline text-gray-600">|</span>
            <p className="text-gray-500 text-sm">
              NPI: <a 
                href="https://npiregistry.cms.hhs.gov/provider-view/1396657813" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-npi-link"
                className="text-[#00f0ff] hover:underline"
              >
                1396657813
              </a>
            </p>
          </div>
          <div className="flex gap-6">
            <a href="#" className="text-gray-500 text-sm hover:text-gray-300 transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-gray-500 text-sm hover:text-gray-300 transition-colors">
              Terms of Service
            </a>
            <a href="#" className="text-gray-500 text-sm hover:text-gray-300 transition-colors">
              HIPAA Compliance
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
